
/**
 * Format an ISO-like or delimited date string to "DD/MM/YYYY".
 *
 * This function accepts an optional date/time string and attempts to extract
 * the date portion, detect the separator and the ordering (YYYY-MM-DD or
 * DD-MM-YYYY), normalize day and month to two digits and return the date in
 * "DD/MM/YYYY" format.
 *
 * Behavior summary:
 * - If `value` is falsy, returns "-" to indicate absence of a date.
 * - Splits off the time portion by whitespace or "T" (e.g. "2003-05-31 00:00:00" or
 *   "2003-05-31T00:00:00" → "2003-05-31").
 * - Determines candidate separators from `inputSeparator` (string or array) or
 *   falls back to common separators ['-', '/', '.'].
 * - Chooses the first candidate separator found in the input; if none match,
 *   tries common fallbacks in this order: '-', '/', '.'.
 * - If no separator can be determined, returns the original `value`.
 * - Expects exactly three date parts after splitting; otherwise returns original `value`.
 * - Detects YYYY-MM-DD when the first part has length 4, or DD-MM-YYYY when the third part has length 4.
 * - Performs basic numeric validation on year, month and day.
 * - Pads day and month to two digits and returns the normalized "DD/MM/YYYY".
 *
 * Notes:
 * - The function does not perform full calendar validation (e.g. it does not check
 *   for valid month range or correct days per month beyond digit checks).
 * - If parsing or validation fails at any step, the original `value` is returned
 *   (except when `value` is falsy — then returns "-").
 *
 * @param value - The input date/time string to format (optional).
 * @param inputSeparator - A separator string or array of separator strings to prefer when parsing the date.
 *                         If omitted, common separators '-', '/', '.' are considered.
 * @returns The formatted date string in "DD/MM/YYYY" on success, "-" if `value` is falsy,
 *          or the original `value` if parsing/validation fails.
 *
 * @example
 * // returns "31/05/2003"
 * formatDate("2003-05-31T00:00:00");
 *
 * @example
 * // returns "31/05/2003"
 * formatDate("31-5-2003", "-");
 *
 * @example
 * // returns original value (parsing fails)
 * formatDate("invalid-date");
 */
const formatDate = (
    value?: string,
    inputSeparator: string | string[] = '-',
    inputFormat?: 'DMY' | 'YMD' | 'MDY',
    FormatLengt?: 'long' | 'short'
): string => {
    if (!value) return '-';

    // separate date from time (supports " ", "T", etc.)
    const datePart = value.split(/[\sT]/)[0].trim();

    // determine candidate separators: use provided or fallback to common ones
    const candidates = Array.isArray(inputSeparator)
        ? inputSeparator
        : typeof inputSeparator === 'string'
        ? [inputSeparator]
        : ['-', '/', '.'];

    // pick the first candidate that exists in the input; if none found, try common fallbacks
    let sep =
        candidates.find((s) => s && datePart.includes(s)) ??
        (datePart.includes('-') ? '-' : datePart.includes('/') ? '/' : datePart.includes('.') ? '.' : null);

    if (!sep) return value;

    const parts = datePart.split(sep);
    if (parts.length !== 3) return value;

    // map parts according to inputFormat if provided, otherwise attempt detection
    let yearStr: string, monthStr: string, dayStr: string;

    if (inputFormat) {
        switch (inputFormat) {
            case 'YMD':
                [yearStr, monthStr, dayStr] = parts;
                break;
            case 'DMY':
                [dayStr, monthStr, yearStr] = parts;
                break;
            case 'MDY':
                [monthStr, dayStr, yearStr] = parts;
                break;
            default:
                return value;
        }
    } else {
        // detect YYYY-MM-DD or DD-MM-YYYY (or same with other separators)
        if (/^\d{4}$/.test(parts[0])) {
            // YYYY-MM-DD
            [yearStr, monthStr, dayStr] = parts;
        } else if (/^\d{4}$/.test(parts[2])) {
            // DD-MM-YYYY
            [dayStr, monthStr, yearStr] = parts;
        } else {
            return value;
        }
    }

    // basic numeric validation
    if (!/^\d{1,4}$/.test(yearStr) || !/^\d{1,2}$/.test(monthStr) || !/^\d{1,2}$/.test(dayStr)) {
        return value;
    }

    const yearNum = Number(yearStr);
    const monthNum = Number(monthStr);
    const dayNum = Number(dayStr);

    if (!Number.isFinite(yearNum) || !Number.isFinite(monthNum) || !Number.isFinite(dayNum)) return value;
    if (monthNum < 1 || monthNum > 12) return value;

    const daysInMonth = new Date(yearNum < 100 ? 2000 + yearNum : yearNum, monthNum, 0).getDate();
    if (dayNum < 1 || dayNum > daysInMonth) return value;

    const dd = dayStr.padStart(2, '0');
    const mm = monthStr.padStart(2, '0');

    // format year according to FormatLengt
    if (FormatLengt === 'short') {
        // return last two digits of year (pad if needed)
        const yy = yearStr.padStart(2, '0').slice(-2);
        return `${dd}/${mm}/${yy}`;
    } else {
        // long (default): ensure 4-digit year
        let yyyy = yearStr;
        if (yyyy.length === 2) {
            // assume 2000-2099 for 2-digit years
            yyyy = '20' + yyyy;
        } else if (yyyy.length < 4) {
            yyyy = yyyy.padStart(4, '0');
        }
        return `${dd}/${mm}/${yyyy}`;
    }
};




const stringToDate = (value?: string, inputSeparator: string | string[] = '-'): Date | null => {
    if (!value) return null;

    // Fast path: let the runtime attempt ISO / full datetime parsing first
    const direct = new Date(value);
    if (!isNaN(direct.getTime())) return direct;

    // isolate the date portion (before any whitespace or "T")
    const dateOnly = value.split(/\s|T/)[0].trim();
    if (!dateOnly) return null;

    // build separators to try
    const candidates = Array.isArray(inputSeparator)
        ? inputSeparator
        : typeof inputSeparator === 'string'
        ? [inputSeparator]
        : ['-', '/', '.'];

    const escapeSep = (s: string) => s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

    const tryParseParts = (yStr: string, mStr: string, dStr: string, timePart?: string) => {
        const y = Number(yStr);
        const m = Number(mStr);
        const d = Number(dStr);
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
        if (m < 1 || m > 12) return null;
        const daysInMonth = new Date(y, m, 0).getDate();
        if (d < 1 || d > daysInMonth) return null;

        // parse optional time part
        if (!timePart) {
            return new Date(y, m - 1, d);
        }

        // extract time and optional timezone
        const timeMatch = timePart.match(/^\s*([0-2]?\d)(?::([0-5]?\d))?(?::([0-5]?\d)(?:\.(\d+))?)?\s*(Z|[+\-]\d{2}:?\d{2})?\s*$/i);
        if (!timeMatch) {
            // If time not in expected form but contains timezone, build ISO and let Date parse
            const tzMatch = timePart.match(/(Z|[+\-]\d{2}:?\d{2})\s*$/i);
            if (tzMatch) {
                const iso = `${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d
                    .toString()
                    .padStart(2, '0')}${'T'}${timePart.trim()}`;
                const dt = new Date(iso);
                return isNaN(dt.getTime()) ? null : dt;
            }
            return new Date(y, m - 1, d);
        }

        const hour = Number(timeMatch[1] ?? 0);
        const minute = Number(timeMatch[2] ?? 0);
        const second = Number(timeMatch[3] ?? 0);
        const ms = timeMatch[4] ? Math.round(Number('0.' + timeMatch[4]) * 1000) : 0;
        const tz = timeMatch[5];

        if (tz) {
            // build ISO with timezone and let Date handle it
            const hh = hour.toString().padStart(2, '0');
            const mm = minute.toString().padStart(2, '0');
            const ss = second.toString().padStart(2, '0');
            const frac = ms ? '.' + ms.toString().padStart(3, '0') : '';
            const iso = `${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d
                .toString()
                .padStart(2, '0')}T${hh}:${mm}:${ss}${frac}${tz}`;
            const dt = new Date(iso);
            return isNaN(dt.getTime()) ? null : dt;
        }

        // no timezone -> construct local date
        return new Date(y, m - 1, d, hour, minute, second, ms);
    };

    // try candidate separators first (maintain order)
    for (const s of candidates) {
        if (!s) continue;
        const esc = escapeSep(s);
        // Try YYYY<sep>MM<sep>DD
        let m = new RegExp(`^(\\d{4})${esc}(\\d{1,2})${esc}(\\d{1,2})(.*)$`).exec(dateOnly + value.slice(dateOnly.length));
        if (m) {
            const timePart = value.slice(dateOnly.length);
            const parsed = tryParseParts(m[1], m[2], m[3], timePart);
            if (parsed) return parsed;
        }
        // Try DD<sep>MM<sep>YYYY
        m = new RegExp(`^(\\d{1,2})${esc}(\\d{1,2})${esc}(\\d{4})(.*)$`).exec(dateOnly + value.slice(dateOnly.length));
        if (m) {
            const timePart = value.slice(dateOnly.length);
            const parsed = tryParseParts(m[3], m[2], m[1], timePart);
            if (parsed) return parsed;
        }
    }

    // fallback: try common separators if none of the provided ones matched
    const fallbacks = ['-', '/', '.'];
    for (const s of fallbacks) {
        if (candidates.includes(s)) continue;
        const esc = escapeSep(s);
        let m = new RegExp(`^(\\d{4})${esc}(\\d{1,2})${esc}(\\d{1,2})(.*)$`).exec(value);
        if (m) {
            const parsed = tryParseParts(m[1], m[2], m[3], value.slice(m[0].length - (m[4] ? m[4].length : 0)));
            if (parsed) return parsed;
        }
        m = new RegExp(`^(\\d{1,2})${esc}(\\d{1,2})${esc}(\\d{4})(.*)$`).exec(value);
        if (m) {
            const parsed = tryParseParts(m[3], m[2], m[1], value.slice(m[0].length - (m[4] ? m[4].length : 0)));
            if (parsed) return parsed;
        }
    }

    return null;
};

export {
    formatDate,
    stringToDate
};